// Conversations workspace — a simple-but-strong shop-management job board.
// One shared filter bar drives four views of the shop's conversations:
//   • List        — sortable table, newest first
//   • Per-Stage   — Kanban (Intake / Inspect / Job / Complete); drag a card one
//                   column to advance/revert the workflow stage
//   • Per-Charger — Kanban by tech; drag a card to reassign it
//   • Calendar    — month grid of appointment entries (colored by stage)
// Cards lead with the actionable JOB STATE (authorization) + real RO#, not
// owner analytics. Card click / "+N more" / bars all open the conversation.
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { Box, Button, Center, Checkbox, Group, Loader, Menu, SegmentedControl, SimpleGrid, Stack, Text } from '@mantine/core';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { IconChevronDown, IconChevronUp, IconDots, IconNote, IconSelector, IconShieldCheck, IconShieldHalf, IconUser } from '@tabler/icons-react';
import { conversationsApi, shopApi, type BoardJobRow } from '../api/client';
import { useAuth } from '../context/AuthContext';
import {
  ConversationFilterBar,
  defaultConvFilters,
  toIsoDate,
  type ConvFilters,
} from '../components/ConversationFilterBar';

// ─── Design tokens (secondary/muted bumped for WCAG-AA contrast on dark) ──────
const BG = '#0C0C0E';
const CARD_BG = '#18181B';
const CARD_BG_HOVER = '#1D1D20';
const BORDER = '#27272A';
const BORDER_HI = '#3F3F46';
const TEXT = '#F4F4F5';
const TEXT_SECONDARY = '#B4B4BD';
const TEXT_MUTED = '#8A8A93';
const BLUE = '#60A5FA';
const GREEN = '#22C55E';
const AMBER = '#F59E0B';
const RED = '#EF4444';

type ViewName = 'list' | 'per-stage' | 'per-charger' | 'calendar' | 'stats';
const VIEWS: { value: ViewName; label: string }[] = [
  { value: 'list', label: 'List' },
  { value: 'per-stage', label: 'Per Stage' },
  { value: 'per-charger', label: 'Per Charger' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'stats', label: 'Statistics' },
];

// Per-stage columns — fixed order, from conversation workflow_stage.
const STAGES: { key: string; label: string; color: string }[] = [
  { key: 'registration', label: 'Intake', color: '#0EA5E9' },
  { key: 'inspection', label: 'Inspect', color: '#8B5CF6' },
  { key: 'quote', label: 'Job', color: '#F59E0B' },
  { key: 'completed', label: 'Complete', color: '#22C55E' },
];
const STAGE_BY_KEY = new Map(STAGES.map((s) => [s.key, s]));
const stageOf = (k: string | null) => (k && STAGE_BY_KEY.has(k) ? k : 'registration');
const stageMeta = (k: string | null) => STAGE_BY_KEY.get(stageOf(k))!;
const UNASSIGNED = 'Unassigned';

// ─── Small helpers ────────────────────────────────────────────────────────────
function dollars(cents: number): string {
  const v = cents / 100;
  const a = Math.abs(v);
  if (a >= 1_000_000) return `$${(v / 1_000_000).toFixed(a >= 10_000_000 ? 0 : 1)}M`;
  if (a >= 1_000) return `$${(v / 1_000).toFixed(a >= 10_000 ? 0 : 1)}k`;
  return `$${Math.round(v)}`;
}
function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.floor(mo / 12)}y`;
}
function fmtDay(s: string | null): string {
  if (!s) return '';
  try {
    return new Date(`${s}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return String(s).slice(0, 10);
  }
}
// Backend returns "—" for a blank customer name; treat that (and empty) as no value
// so labels never render a bare em-dash.
function clean(s: string | null): string {
  const t = (s ?? '').trim();
  return t === '—' ? '' : t;
}
// Best single-line identity for a conversation: real name → vehicle → id.
function rowLabel(j: BoardJobRow): string {
  return clean(j.customer_name) || clean(j.vehicle) || `Conversation #${j.conversation_id}`;
}
function approvalOf(j: BoardJobRow): number | null {
  const den = j.cents_approved + j.cents_declined;
  return den ? Math.round((j.cents_approved / den) * 100) : null;
}
function colKpis(rows: BoardJobRow[]): { revenue: number; appr: number | null; legal: number | null } {
  const revenue = rows.reduce((s, j) => s + j.total_cents, 0);
  const ca = rows.reduce((s, j) => s + j.cents_approved, 0);
  const cd = rows.reduce((s, j) => s + j.cents_declined, 0);
  const apDen = ca + cd;
  const legalTotal = rows.filter((j) => j.quote_legal_safe !== null).length;
  const legalSafe = rows.filter((j) => j.quote_legal_safe === true).length;
  return {
    revenue,
    appr: apDen ? Math.round((ca / apDen) * 100) : null,
    legal: legalTotal ? Math.round((legalSafe / legalTotal) * 100) : null,
  };
}
function LegalCell({ safe }: { safe: boolean | null }) {
  if (safe === true) return <Group gap={3} wrap="nowrap"><IconShieldCheck size={13} color={GREEN} /><Text size="xs" c={GREEN}>Yes</Text></Group>;
  if (safe === false) return <Group gap={3} wrap="nowrap"><IconShieldHalf size={13} color={AMBER} /><Text size="xs" c={AMBER}>No</Text></Group>;
  return <Text size="xs" c={TEXT_MUTED}>—</Text>;
}
// Tap-to-call phone (works on tablet/phone at the bay); never opens the row.
function PhoneLink({ phone, size = '12px' }: { phone: string; size?: string }) {
  return (
    <Text component="a" href={`tel:${phone}`} onClick={(e) => e.stopPropagation()} title={`Call ${phone}`} size={size} c={TEXT_MUTED} style={{ textDecoration: 'none', flexShrink: 0, cursor: 'pointer' }}>
      {phone}
    </Text>
  );
}

// ─── Filter persistence (URL + localStorage so a refresh / shared link keeps state) ──
const LS_FILTERS = 'haist-conv-filters';
function encodeFilters(f: ConvFilters): Record<string, string> {
  const o: Record<string, string> = { basis: f.dateBasis };
  if (f.q.trim()) o.q = f.q.trim();
  const from = toIsoDate(f.from);
  if (from) o.from = from;
  const to = toIsoDate(f.to);
  if (to) o.to = to;
  if (f.techIds.length) o.techs = f.techIds.join(',');
  return o;
}
function decodeFilters(get: (k: string) => string | null): ConvFilters | null {
  const basis = get('basis');
  const q = get('q');
  const from = get('from');
  const to = get('to');
  const techs = get('techs');
  if (!basis && !q && !from && !to && !techs) return null;
  const d = (s: string | null) => (s ? new Date(`${s}T00:00:00`) : null);
  return {
    q: q ?? '',
    from: d(from),
    to: d(to),
    dateBasis: basis === 'intake' ? 'intake' : 'complete',
    techIds: techs ? techs.split(',').map(Number).filter((n) => !Number.isNaN(n)) : [],
  };
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
const AVATAR_COLORS = ['#2563EB', '#7C3AED', '#DB2777', '#D97706', '#059669', '#0891B2', '#DC2626', '#CA8A04'];
function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function Avatar({ name, size = 20 }: { name: string; size?: number }) {
  return (
    <Box
      title={name}
      style={{
        width: size, height: size, borderRadius: '50%', background: avatarColor(name), color: '#fff',
        fontSize: Math.round(size * 0.42), fontWeight: 700, display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0, lineHeight: 1,
      }}
    >
      {initials(name)}
    </Box>
  );
}
function AvatarStack({ names, size = 20 }: { names: string[]; size?: number }) {
  if (!names.length) return null;
  return (
    <Group gap={0} wrap="nowrap" style={{ flexShrink: 0 }}>
      {names.slice(0, 3).map((n, i) => (
        <Box key={n} style={{ marginLeft: i === 0 ? 0 : -6, borderRadius: '50%', boxShadow: `0 0 0 1.5px ${CARD_BG}` }}>
          <Avatar name={n} size={size} />
        </Box>
      ))}
      {names.length > 3 && <Text size="10px" c={TEXT_MUTED} ml={4}>+{names.length - 3}</Text>}
    </Group>
  );
}

// The actionable "can I work on this?" state, derived from authorization data.
type JobState = { label: string; color: string };
function jobState(j: BoardJobRow): JobState {
  if (j.quote_legal_safe === false) return { label: 'Needs Auth', color: AMBER };
  if (j.quote_legal_safe === true) return { label: 'Authorized', color: GREEN };
  return { label: 'Estimate', color: TEXT_MUTED };
}
function StateBadge({ j }: { j: BoardJobRow }) {
  const s = jobState(j);
  return (
    <Box style={{ padding: '2px 8px', borderRadius: 5, background: `${s.color}22`, color: s.color, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>
      {s.label}
    </Box>
  );
}
function StageChip({ stage }: { stage: string | null }) {
  const m = stageMeta(stage);
  return <Text size="11px" c={m.color} fw={600}>{m.label}</Text>;
}

// Per-row ⋮ menu: open · call/text customer · reassign to a tech (card & list row).
function RowMenu({
  members,
  phone,
  onOpen,
  onReassign,
}: {
  members: { id: number; name: string }[];
  phone?: string | null;
  onOpen: () => void;
  onReassign: (techName: string) => void;
}) {
  const item = { color: TEXT, fontSize: 13 };
  return (
    <Menu position="bottom-end" withinPortal shadow="md" width={190}>
      <Menu.Target>
        <Box
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: TEXT_MUTED, flexShrink: 0, padding: 2 }}
        >
          <IconDots size={16} />
        </Box>
      </Menu.Target>
      <Menu.Dropdown style={{ background: '#1C1C1F', border: `1px solid ${BORDER}` }}>
        <Menu.Item onClick={(e) => { e.stopPropagation(); onOpen(); }} styles={{ item }}>Open</Menu.Item>
        {phone && (
          <>
            <Menu.Item component="a" href={`tel:${phone}`} onClick={(e) => e.stopPropagation()} styles={{ item }}>Call customer</Menu.Item>
            <Menu.Item component="a" href={`sms:${phone}`} onClick={(e) => e.stopPropagation()} styles={{ item }}>Text customer</Menu.Item>
          </>
        )}
        <Menu.Divider />
        <Menu.Label c={TEXT_MUTED}>Reassign to</Menu.Label>
        <Box style={{ maxHeight: 200, overflowY: 'auto' }}>
          {members.map((m) => (
            <Menu.Item key={m.id} onClick={(e) => { e.stopPropagation(); onReassign(m.name); }} styles={{ item }}>{m.name}</Menu.Item>
          ))}
          <Menu.Item onClick={(e) => { e.stopPropagation(); onReassign(UNASSIGNED); }} styles={{ item: { color: TEXT_MUTED, fontSize: 13 } }}>Unassigned</Menu.Item>
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
}

// ─── Card (board) ─────────────────────────────────────────────────────────────
function ConvCard({
  j,
  members,
  onOpen,
  onReassign,
}: {
  j: BoardJobRow;
  members: { id: number; name: string }[];
  onOpen: () => void;
  onReassign: (techName: string) => void;
}) {
  return (
    <Box
      onClick={onOpen}
      style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '9px 11px', transition: 'border-color 120ms ease, background 120ms ease' }}
      onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.borderColor = BORDER_HI; el.style.background = CARD_BG_HOVER; }}
      onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.borderColor = BORDER; el.style.background = CARD_BG; }}
    >
      <Group justify="space-between" wrap="nowrap" gap={6} mb={7}>
        <StateBadge j={j} />
        <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
          <AvatarStack names={j.charger_names} />
          <Text size="11px" c={TEXT_MUTED} style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }} title={j.created_at ?? undefined}>
            {timeAgo(j.created_at)}
          </Text>
          <RowMenu members={members} phone={j.phone} onOpen={onOpen} onReassign={onReassign} />
        </Group>
      </Group>

      <Group gap={6} wrap="nowrap" mb={1}>
        <IconUser size={13} color={TEXT_MUTED} style={{ flexShrink: 0 }} />
        <Text size="13px" fw={700} c={TEXT} truncate>{clean(j.customer_name) || clean(j.vehicle) || rowLabel(j)}</Text>
        {j.phone && <PhoneLink phone={j.phone} />}
      </Group>
      <Text size="12px" c={TEXT_SECONDARY} truncate mb={8} style={{ paddingLeft: 19, minHeight: 16 }}>{clean(j.customer_name) ? clean(j.vehicle) : ''}</Text>

      <Group justify="space-between" wrap="nowrap" align="center" style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 7 }}>
        <Group gap={6} wrap="nowrap">
          <StageChip stage={j.workflow_stage} />
          {j.has_memo && <IconNote size={13} color={TEXT_MUTED} />}
        </Group>
        <Text size="14px" fw={700} c={TEXT} style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{dollars(j.total_cents)}</Text>
      </Group>
    </Box>
  );
}

// Draggable wrapper (board views only).
function DraggableCard({ children, convId, stage, cols }: { children: ReactNode; convId: number; stage: string | null; cols: string[] }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `conv-${convId}`,
    data: { convId, stage: stageOf(stage), cols },
  });
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.4 : 1, cursor: 'grab', touchAction: 'none' }}
    >
      {children}
    </Box>
  );
}

function EmptyState({ filters }: { filters: ConvFilters }) {
  const bits: string[] = [];
  if (filters.q.trim()) bits.push(`matching "${filters.q.trim()}"`);
  if (filters.techIds.length) bits.push('for the selected tech');
  const range = filters.from && filters.to ? ` from ${fmtDay(toIsoDate(filters.from)!)} to ${fmtDay(toIsoDate(filters.to)!)}` : '';
  return (
    <Box py={56} style={{ textAlign: 'center' }}>
      <Text size="sm" c={TEXT_SECONDARY}>No conversations{bits.length ? ` ${bits.join(' ')}` : ''}{range}.</Text>
      <Text size="xs" c={TEXT_MUTED} mt={4}>Try widening the date range or clearing filters.</Text>
    </Box>
  );
}

const boardRowStyle: React.CSSProperties = { display: 'flex', gap: 12, paddingBottom: 8, alignItems: 'flex-start', minWidth: 'min-content' };

// ─── Board column (droppable) ─────────────────────────────────────────────────
function BoardColumn({
  colId,
  name,
  accent,
  rows,
  members,
  onOpen,
  onReassign,
  draggable,
}: {
  colId: string;
  name: string;
  accent?: string;
  rows: BoardJobRow[];
  members: { id: number; name: string }[];
  onOpen: (id: number) => void;
  onReassign: (convId: number, techName: string) => void;
  draggable: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: colId });
  const k = colKpis(rows);
  return (
    <Box ref={setNodeRef} style={{ minWidth: 252, width: 252, flexShrink: 0, borderRadius: 10, padding: 4, background: isOver ? 'rgba(96,165,250,0.08)' : 'transparent', outline: isOver ? `1px dashed ${BLUE}` : 'none', transition: 'background 100ms' }}>
      <Box style={{ position: 'sticky', top: 0, zIndex: 1, background: BG, paddingBottom: 6 }}>
        <Group justify="space-between" mb={3} px={4} wrap="nowrap" gap={6}>
          <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
            {accent && <Box style={{ width: 7, height: 7, borderRadius: 4, background: accent, flexShrink: 0 }} />}
            <Text size="13px" fw={700} c={TEXT} truncate>{name}</Text>
            <Text size="11px" c={TEXT_MUTED} fw={600} style={{ flexShrink: 0 }}>{rows.length}</Text>
          </Group>
          <Text size="12px" c={TEXT_SECONDARY} fw={700} style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{dollars(k.revenue)}</Text>
        </Group>
        <Group gap={10} px={4} mb={3} wrap="nowrap">
          <Text size="10px" c={TEXT_MUTED}>Appr <Text span c={k.appr == null ? TEXT_MUTED : '#8B5CF6'} fw={700}>{k.appr == null ? '—' : `${k.appr}%`}</Text></Text>
          <Text size="10px" c={TEXT_MUTED}>Legal <Text span c={k.legal == null ? TEXT_MUTED : k.legal >= 80 ? GREEN : AMBER} fw={700}>{k.legal == null ? '—' : `${k.legal}%`}</Text></Text>
        </Group>
      </Box>
      <Stack gap={6} px={2}>
        {rows.map((j) => {
          const card = <ConvCard j={j} members={members} onOpen={() => onOpen(j.conversation_id)} onReassign={(t) => onReassign(j.conversation_id, t)} />;
          return draggable ? (
            <DraggableCard key={j.conversation_id} convId={j.conversation_id} stage={j.workflow_stage} cols={j.charger_names.length ? j.charger_names : [UNASSIGNED]}>{card}</DraggableCard>
          ) : (
            <Box key={j.conversation_id}>{card}</Box>
          );
        })}
      </Stack>
    </Box>
  );
}

// ─── List view (sortable) ─────────────────────────────────────────────────────
type SortKey = 'date' | 'tech' | 'stage' | 'revenue' | 'appr';
const COL = { tech: 60, stage: 80, state: 96, revenue: 86, appr: 58, legal: 64, date: 56, menu: 28 };
function SortHeader({ w, label, k, sort, dir, onSort, right }: { w: number; label: string; k: SortKey; sort: SortKey; dir: 'asc' | 'desc'; onSort: (k: SortKey) => void; right?: boolean }) {
  const active = sort === k;
  return (
    <Box onClick={() => onSort(k)} style={{ width: w, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2, justifyContent: right ? 'flex-end' : 'flex-start', cursor: 'pointer', userSelect: 'none' }}>
      <Text size="10px" c={active ? TEXT_SECONDARY : TEXT_MUTED} fw={700} tt="uppercase">{label}</Text>
      {active ? (dir === 'asc' ? <IconChevronUp size={11} color={TEXT_SECONDARY} /> : <IconChevronDown size={11} color={TEXT_SECONDARY} />) : <IconSelector size={11} color={TEXT_MUTED} />}
    </Box>
  );
}
const CHK = 30;
function ListView({ rows, members, onOpen, onReassign, onBulkReassign, onBulkArchive }: { rows: BoardJobRow[]; members: { id: number; name: string }[]; onOpen: (id: number) => void; onReassign: (convId: number, techName: string) => void; onBulkReassign: (ids: number[], techName: string) => void; onBulkArchive: (ids: number[]) => void }) {
  const [sort, setSort] = useState<SortKey>('date');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const onSort = (k: SortKey) => {
    if (k === sort) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(k); setDir(k === 'date' || k === 'revenue' || k === 'appr' ? 'desc' : 'asc'); }
  };
  const sorted = useMemo(() => {
    const sgn = dir === 'asc' ? 1 : -1;
    const val = (j: BoardJobRow): string | number => {
      switch (sort) {
        case 'tech': return (j.charger_names[0] || '~').toLowerCase();
        case 'stage': return STAGES.findIndex((s) => s.key === stageOf(j.workflow_stage));
        case 'revenue': return j.total_cents;
        case 'appr': return approvalOf(j) ?? -1;
        default: return (j.complete_date || j.intake_date || '');
      }
    };
    return [...rows].sort((a, b) => { const x = val(a), y = val(b); return x < y ? -sgn : x > y ? sgn : 0; });
  }, [rows, sort, dir]);

  const selIds = useMemo(() => sorted.filter((j) => selected.has(j.conversation_id)).map((j) => j.conversation_id), [sorted, selected]);
  const allChecked = sorted.length > 0 && selIds.length === sorted.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(sorted.map((j) => j.conversation_id)));
  const toggleOne = (id: number) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  const chkStyles = { input: { background: 'transparent', borderColor: '#52525B', cursor: 'pointer' }, root: { cursor: 'pointer' } } as const;

  if (!rows.length) return null;
  return (
    <>
      <Box style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: `1px solid ${BORDER}`, background: '#141416' }}>
          <Box style={{ width: CHK, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
            <Checkbox size="xs" color="blue" checked={allChecked} indeterminate={selIds.length > 0 && !allChecked} onChange={toggleAll} styles={chkStyles} />
          </Box>
          <Text style={{ flex: 1, minWidth: 0 }} size="10px" c={TEXT_MUTED} fw={700} tt="uppercase">Customer / Vehicle</Text>
          <SortHeader w={COL.tech} label="Tech" k="tech" sort={sort} dir={dir} onSort={onSort} />
          <SortHeader w={COL.stage} label="Stage" k="stage" sort={sort} dir={dir} onSort={onSort} />
          <Text style={{ width: COL.state, flexShrink: 0 }} size="10px" c={TEXT_MUTED} fw={700} tt="uppercase">State</Text>
          <SortHeader w={COL.revenue} label="Revenue" k="revenue" sort={sort} dir={dir} onSort={onSort} right />
          <SortHeader w={COL.appr} label="Appr" k="appr" sort={sort} dir={dir} onSort={onSort} right />
          <Text style={{ width: COL.legal, flexShrink: 0, textAlign: 'right' }} size="10px" c={TEXT_MUTED} fw={700} tt="uppercase">Legal</Text>
          <SortHeader w={COL.date} label="Date" k="date" sort={sort} dir={dir} onSort={onSort} right />
          <Box style={{ width: COL.menu, flexShrink: 0 }} />
        </Box>
        {sorted.map((j) => (
          <Box
            key={j.conversation_id}
            onClick={() => onOpen(j.conversation_id)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: `1px solid ${BORDER}`, cursor: 'pointer', transition: 'background 0.1s', background: selected.has(j.conversation_id) ? 'rgba(96,165,250,0.07)' : 'transparent' }}
            onMouseEnter={(e) => { if (!selected.has(j.conversation_id)) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)'; }}
            onMouseLeave={(e) => { if (!selected.has(j.conversation_id)) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            <Box style={{ width: CHK, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
              <Checkbox size="xs" color="blue" checked={selected.has(j.conversation_id)} onChange={() => toggleOne(j.conversation_id)} styles={chkStyles} />
            </Box>
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Group gap={6} wrap="nowrap">
                <IconUser size={13} color={TEXT_MUTED} style={{ flexShrink: 0 }} />
                <Text size="sm" fw={700} c={TEXT} truncate>{clean(j.customer_name) || clean(j.vehicle) || rowLabel(j)}</Text>
                {j.phone && <PhoneLink phone={j.phone} size="xs" />}
                {j.has_memo && <IconNote size={12} color={TEXT_MUTED} style={{ flexShrink: 0 }} />}
              </Group>
              <Text size="xs" c={TEXT_SECONDARY} truncate style={{ paddingLeft: 19 }}>{clean(j.customer_name) ? clean(j.vehicle) : ''}</Text>
            </Box>
            <Box style={{ width: COL.tech, flexShrink: 0 }}>{j.charger_names.length ? <AvatarStack names={j.charger_names} size={22} /> : <Text size="xs" c={TEXT_MUTED}>—</Text>}</Box>
            <Box style={{ width: COL.stage, flexShrink: 0 }}><StageChip stage={j.workflow_stage} /></Box>
            <Box style={{ width: COL.state, flexShrink: 0 }}><StateBadge j={j} /></Box>
            <Text size="sm" fw={700} c={TEXT} style={{ width: COL.revenue, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{dollars(j.total_cents)}</Text>
            {(() => { const a = approvalOf(j); return <Text size="xs" fw={600} c={a == null ? TEXT_MUTED : '#A78BFA'} style={{ width: COL.appr, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{a == null ? '—' : `${a}%`}</Text>; })()}
            <Box style={{ width: COL.legal, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}><LegalCell safe={j.quote_legal_safe} /></Box>
            <Text size="xs" c={TEXT_MUTED} style={{ width: COL.date, flexShrink: 0, textAlign: 'right' }}>{fmtDay(j.complete_date || j.intake_date)}</Text>
            <Box style={{ width: COL.menu, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
              <RowMenu members={members} phone={j.phone} onOpen={() => onOpen(j.conversation_id)} onReassign={(t) => onReassign(j.conversation_id, t)} />
            </Box>
          </Box>
        ))}
      </Box>

      {selIds.length > 0 && (
        <Box style={{ position: 'sticky', bottom: 0, zIndex: 5, marginTop: 10, background: '#1C1C1F', border: `1px solid ${BORDER_HI}`, borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 -6px 20px rgba(0,0,0,0.45)' }}>
          <Text size="sm" fw={600} c={TEXT}>{selIds.length} selected</Text>
          <Menu position="top-start" withinPortal shadow="md" width={190}>
            <Menu.Target><Button size="xs" variant="default" styles={{ root: { background: 'transparent', border: `1px solid ${BORDER_HI}`, color: TEXT } }}>Reassign</Button></Menu.Target>
            <Menu.Dropdown style={{ background: '#1C1C1F', border: `1px solid ${BORDER}` }}>
              <Box style={{ maxHeight: 240, overflowY: 'auto' }}>
                {members.map((m) => (
                  <Menu.Item key={m.id} onClick={() => { onBulkReassign(selIds, m.name); clearSel(); }} styles={{ item: { color: TEXT, fontSize: 13 } }}>{m.name}</Menu.Item>
                ))}
                <Menu.Item onClick={() => { onBulkReassign(selIds, UNASSIGNED); clearSel(); }} styles={{ item: { color: TEXT_MUTED, fontSize: 13 } }}>Unassigned</Menu.Item>
              </Box>
            </Menu.Dropdown>
          </Menu>
          <Button size="xs" variant="default" onClick={() => { onBulkArchive(selIds); clearSel(); }} styles={{ root: { background: 'transparent', border: `1px solid ${BORDER_HI}`, color: TEXT } }}>Archive</Button>
          <Box style={{ flex: 1 }} />
          <Button size="xs" variant="subtle" onClick={clearSel} styles={{ root: { color: TEXT_MUTED } }}>Clear</Button>
        </Box>
      )}
    </>
  );
}

// ─── Statistics (List + the numbers added up) ─────────────────────────────────
// One compact tile. Big tabular value, uppercase label, optional sub-hint.
function StatTile({ label, value, color, hint }: { label: string; value: string; color?: string; hint?: string }) {
  return (
    <Box style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 10, padding: '14px 18px' }}>
      <Text size="xs" c={TEXT_MUTED} fw={600} tt="uppercase" style={{ letterSpacing: 0.6 }}>{label}</Text>
      <Text size="24px" fw={700} c={color ?? TEXT} mt={4} style={{ lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</Text>
      {hint && <Text size="xs" c={TEXT_MUTED} mt={3}>{hint}</Text>}
    </Box>
  );
}
// The "added up" numbers across the loaded rows — same KPI language as the List
// columns (revenue / approval / legal), via the existing colKpis() helper.
function TotalsStrip({ rows, total }: { rows: BoardJobRow[]; total: number }) {
  const k = colKpis(rows);
  const avg = rows.length ? Math.round(k.revenue / rows.length) : 0;
  const legalColor = k.legal == null ? TEXT_MUTED : k.legal >= 80 ? GREEN : AMBER;
  const capped = total > rows.length;
  return (
    <Stack gap={8} mb={14}>
      <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm">
        <StatTile label="Jobs" value={String(rows.length)} />
        <StatTile label="Revenue" value={dollars(k.revenue)} color={GREEN} />
        <StatTile label="Avg ticket" value={dollars(avg)} hint="per job" />
        <StatTile label="Approval" value={k.appr == null ? '—' : `${k.appr}%`} color={k.appr == null ? TEXT_MUTED : '#A78BFA'} hint="by $ of work" />
        <StatTile label="Legal-safe" value={k.legal == null ? '—' : `${k.legal}%`} color={legalColor} hint="of quoted jobs" />
      </SimpleGrid>
      {capped && (
        <Text size="xs" c={TEXT_MUTED}>Totals cover the most recent {rows.length} of {total} jobs — narrow the date range for an exact total.</Text>
      )}
    </Stack>
  );
}

// ─── Calendar (appointment entries) ───────────────────────────────────────────
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const isoOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function CalendarView({ rows, dateBasis, from, to, onOpen, onPickDay }: { rows: BoardJobRow[]; dateBasis: 'complete' | 'intake'; from: Date; to: Date; onOpen: (id: number) => void; onPickDay: (d: Date) => void }) {
  const byDay = useMemo(() => {
    const m = new Map<string, BoardJobRow[]>();
    for (const j of rows) {
      const day = dateBasis === 'complete' ? j.complete_date || j.intake_date : j.intake_date;
      if (!day) continue;
      (m.get(day) ?? m.set(day, []).get(day)!).push(j);
    }
    return m;
  }, [rows, dateBasis]);
  // Fit the grid to the SELECTED range: first cell = FROM, last cell = TO, every
  // day in between — no leading/trailing week padding, no greyed out-of-range
  // cells. 7-day range → one row of 7; 60-day range → ~9 continuous rows. The
  // 7-wide grid is kept (below) so it still reads as a calendar; rows start on
  // the FROM weekday rather than Sunday, so the weekday is shown per-cell.
  const cells = useMemo(() => {
    const start = startOfDay(from);
    const end = startOfDay(to);
    const out: Date[] = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) out.push(new Date(d));
    return out;
  }, [from, to]);

  const SHOW = 4;
  const rangeLabel = `${from.toLocaleString(undefined, { month: 'short', day: 'numeric' })} – ${to.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  return (
    <Box p="md" style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 10 }}>
      <Group justify="space-between" mb={12} wrap="wrap" gap={8}>
        <Text size="sm" fw={700} c={TEXT}>{rangeLabel}</Text>
        <Group gap={12} wrap="wrap">
          {STAGES.map((s) => (
            <Group key={s.key} gap={5} wrap="nowrap">
              <Box style={{ width: 8, height: 8, borderRadius: 3, background: s.color }} />
              <Text size="11px" c={TEXT_SECONDARY}>{s.label}</Text>
            </Group>
          ))}
        </Group>
      </Group>
      <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {cells.map((d, i) => {
          const list = byDay.get(isoOf(d)) ?? [];
          const isToday = new Date().toDateString() === d.toDateString();
          const isMonthStart = d.getDate() === 1;
          const weekday = d.toLocaleString(undefined, { weekday: 'short' });
          // Rows no longer Sunday-align, so label each cell with its weekday +
          // date (month name on the 1st as a divider).
          const dayLabel = isMonthStart
            ? `${weekday} ${d.toLocaleString(undefined, { month: 'short' })} ${d.getDate()}`
            : `${weekday} ${d.getDate()}`;
          return (
            <Box key={i} style={{ minHeight: 132, padding: 7, borderRadius: 7, border: isToday ? `2px solid ${BLUE}` : `1px solid ${BORDER}`, background: '#161619' }}>
              <Group justify="space-between" wrap="nowrap" mb={4}>
                <Text size="11px" c={isToday ? BLUE : isMonthStart ? TEXT : TEXT_SECONDARY} fw={isToday || isMonthStart ? 700 : 500}>{dayLabel}</Text>
                {list.length > 0 && <Text size="10px" c={TEXT_MUTED} fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>{list.length}</Text>}
              </Group>
              <Stack gap={3}>
                {list.slice(0, SHOW).map((j) => {
                  const m = stageMeta(j.workflow_stage);
                  return (
                    <Box
                      key={j.conversation_id}
                      onClick={(e) => { e.stopPropagation(); onOpen(j.conversation_id); }}
                      title={[clean(j.customer_name), clean(j.vehicle)].filter(Boolean).join(' · ') || rowLabel(j)}
                      style={{ background: `${m.color}24`, borderLeft: `3px solid ${m.color}`, borderRadius: 4, padding: '3px 6px', cursor: 'pointer', overflow: 'hidden', transition: 'background 100ms' }}
                      onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = `${m.color}3a`)}
                      onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = `${m.color}24`)}
                    >
                      <Text size="11px" c={TEXT} truncate style={{ lineHeight: 1.35, fontWeight: 500 }}>{rowLabel(j)}</Text>
                    </Box>
                  );
                })}
                {list.length > SHOW && (
                  <Text size="10px" c={TEXT_MUTED} fw={600} style={{ cursor: 'pointer', paddingLeft: 2 }} onClick={() => onPickDay(d)}>+{list.length - SHOW} more</Text>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Skeletons ────────────────────────────────────────────────────────────────
function BoardSkeleton() {
  return (
    <Box style={boardRowStyle}>
      {[0, 1, 2, 3].map((c) => (
        <Box key={c} style={{ minWidth: 252, width: 252, flexShrink: 0 }}>
          <Box style={{ height: 14, width: '50%', borderRadius: 4, background: 'rgba(255,255,255,0.05)', marginBottom: 10 }} />
          <Stack gap={6}>
            {[0, 1, 2].map((i) => <Box key={i} className="conv-shimmer" style={{ height: 96, borderRadius: 10 }} />)}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export function ConversationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (VIEWS.some((v) => v.value === searchParams.get('view')) ? searchParams.get('view') : 'list') as ViewName;
  // Filters survive refresh + are shareable: seed from the URL, else last-used
  // (localStorage), else defaults.
  const [filters, setFiltersState] = useState<ConvFilters>(() => {
    const fromUrl = decodeFilters((k) => searchParams.get(k));
    if (fromUrl) return fromUrl;
    try {
      const ls = localStorage.getItem(LS_FILTERS);
      if (ls) { const o = JSON.parse(ls) as Record<string, string>; const f = decodeFilters((k) => o[k] ?? null); if (f) return f; }
    } catch { /* ignore */ }
    return defaultConvFilters();
  });
  const setFilters = useCallback((f: ConvFilters) => {
    setFiltersState(f);
    const enc = encodeFilters(f);
    const next = new URLSearchParams(searchParams);
    ['q', 'from', 'to', 'basis', 'techs'].forEach((k) => next.delete(k));
    Object.entries(enc).forEach(([k, v]) => next.set(k, v));
    setSearchParams(next, { replace: true });
    try { localStorage.setItem(LS_FILTERS, JSON.stringify(enc)); } catch { /* ignore */ }
  }, [searchParams, setSearchParams]);
  // Optimistic drag overrides: convId → new stage / new tech (cleared after revalidate).
  const [override, setOverride] = useState<Record<number, { stage?: string; techName?: string }>>({});

  const { data: members } = useSWR('shop-members', () => shopApi.listMembers().then((m) => m.map((x) => ({ id: x.id, name: x.name }))));
  const memberList = members ?? [];

  const params = useMemo(
    () => ({
      q: filters.q.trim() || undefined,
      from: toIsoDate(filters.from),
      to: toIsoDate(filters.to),
      date_basis: filters.dateBasis,
      charger_user_ids: filters.techIds.length ? filters.techIds.join(',') : undefined,
    }),
    [filters],
  );
  const paramKey = JSON.stringify(params);
  // Live board — poll so cards reflect the floor without a manual reload.
  const { data: board, isLoading, mutate } = useSWR(['conv-board', paramKey], () => conversationsApi.board(params), { refreshInterval: 20000, revalidateOnFocus: true });

  // Apply optimistic overrides so a dragged card jumps immediately.
  const rows = useMemo(() => {
    const base = board?.rows ?? [];
    if (!Object.keys(override).length) return base;
    return base.map((j) => {
      const o = override[j.conversation_id];
      if (!o) return j;
      return {
        ...j,
        workflow_stage: o.stage ?? j.workflow_stage,
        charger_names: o.techName !== undefined ? (o.techName === UNASSIGNED ? [] : [o.techName]) : j.charger_names,
      };
    });
  }, [board, override]);

  const open = (id: number) => navigate(`/shop/conversations/${id}`);
  const setView = (v: ViewName) => { const n = new URLSearchParams(searchParams); n.set('view', v); setSearchParams(n); };
  const pickDay = (d: Date) => { setFilters({ ...filters, from: d, to: d }); setView('list'); };
  const myJobsToday = () => { const t = new Date(); setFilters({ ...filters, from: t, to: t, techIds: user?.id ? [user.id] : [] }); };
  const isMineToday = !!user?.id && filters.techIds.length === 1 && filters.techIds[0] === user.id
    && !!filters.from && !!filters.to && toIsoDate(filters.from) === toIsoDate(new Date()) && toIsoDate(filters.to) === toIsoDate(new Date());

  const bulkReassign = useCallback(async (ids: number[], techName: string) => {
    const techId = techName === UNASSIGNED ? null : memberList.find((m) => m.name === techName)?.id ?? null;
    if (techName !== UNASSIGNED && techId == null) return;
    setOverride((o) => { const n = { ...o }; ids.forEach((id) => { n[id] = { ...n[id], techName }; }); return n; });
    try { await Promise.all(ids.map((id) => conversationsApi.patch(id, { user_ids: techId != null ? [techId] : [] }))); }
    finally { await mutate(); setOverride((o) => { const n = { ...o }; ids.forEach((id) => delete n[id]); return n; }); }
  }, [memberList, mutate]);
  const bulkArchive = useCallback(async (ids: number[]) => {
    try { await Promise.all(ids.map((id) => conversationsApi.patch(id, { status: 'archived' }))); }
    finally { await mutate(); }
  }, [mutate]);

  const reassign = useCallback(async (convId: number, techName: string) => {
    const techId = techName === UNASSIGNED ? null : memberList.find((m) => m.name === techName)?.id ?? null;
    if (techName !== UNASSIGNED && techId == null) return;
    setOverride((o) => ({ ...o, [convId]: { ...o[convId], techName } }));
    try {
      await conversationsApi.patch(convId, { user_ids: techId != null ? [techId] : [] });
    } finally {
      await mutate();
      setOverride((o) => { const n = { ...o }; delete n[convId]; return n; });
    }
  }, [memberList, mutate]);

  const moveStage = useCallback(async (convId: number, fromStage: string, toStage: string) => {
    const fi = STAGES.findIndex((s) => s.key === fromStage);
    const ti = STAGES.findIndex((s) => s.key === toStage);
    if (ti < 0 || Math.abs(ti - fi) !== 1) return; // adjacent only — non-adjacent snaps back (no-op)
    const action = ti > fi ? 'advance' : 'revert';
    setOverride((o) => ({ ...o, [convId]: { ...o[convId], stage: toStage } }));
    try {
      await conversationsApi.transition(convId, action);
    } finally {
      await mutate();
      setOverride((o) => { const n = { ...o }; delete n[convId]; return n; });
    }
  }, [mutate]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const onDragEnd = useCallback((e: DragEndEvent, kind: 'stage' | 'charger') => {
    const convId = e.active.data.current?.convId as number | undefined;
    const overId = e.over?.id?.toString();
    if (!convId || !overId) return;
    if (kind === 'charger' && overId.startsWith('tech:')) {
      const targetName = overId.slice(5);
      const cols = (e.active.data.current?.cols as string[]) || [];
      if (cols.includes(targetName)) return; // already there
      reassign(convId, targetName);
    } else if (kind === 'stage' && overId.startsWith('stage:')) {
      const targetStage = overId.slice(6);
      const fromStage = (e.active.data.current?.stage as string) || 'registration';
      if (targetStage !== fromStage) moveStage(convId, fromStage, targetStage);
    }
  }, [reassign, moveStage]);

  // Group rows for the boards.
  const stageCols = useMemo(() => {
    const m = new Map<string, BoardJobRow[]>();
    STAGES.forEach((s) => m.set(s.key, []));
    for (const j of rows) m.get(stageOf(j.workflow_stage))!.push(j);
    return m;
  }, [rows]);
  const chargerCols = useMemo(() => {
    const m = new Map<string, BoardJobRow[]>();
    // Seed a column for EVERY shop member first, so a tech with zero jobs in the
    // selected window still shows as an (empty) column instead of disappearing.
    for (const mem of memberList) m.set(mem.name, []);
    for (const j of rows) {
      const names = j.charger_names.length ? j.charger_names : [UNASSIGNED];
      for (const n of names) (m.get(n) ?? m.set(n, []).get(n)!).push(j);
    }
    return Array.from(m.entries())
      .map(([name, list]) => ({ name, list, revenue: list.reduce((s, j) => s + j.total_cents, 0) }))
      .sort((a, b) => (a.name === UNASSIGNED ? 1 : b.name === UNASSIGNED ? -1 : b.revenue - a.revenue));
  }, [rows, memberList]);

  const calFrom = filters.from ?? (() => { const f = new Date(); f.setDate(f.getDate() - 29); return f; })();
  const calTo = filters.to ?? new Date();
  const empty = !isLoading && rows.length === 0;

  return (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column', background: BG }}>
      <style>{`@keyframes convShimmer{0%{background:rgba(255,255,255,0.03)}50%{background:rgba(255,255,255,0.07)}100%{background:rgba(255,255,255,0.03)}}.conv-shimmer{animation:convShimmer 1.6s ease-in-out infinite}`}</style>
      {/* Fixed header: title + view tabs + shared filter bar */}
      <Box style={{ flexShrink: 0, padding: '16px 20px 10px' }}>
        <Group justify="space-between" align="center" mb={12} wrap="wrap" gap={10}>
          <Group gap={10} wrap="nowrap" align="center">
            <Text size="xl" fw={700} c={TEXT}>Conversations</Text>
            {user?.id && (
              <Button
                size="xs"
                variant="default"
                onClick={myJobsToday}
                styles={{ root: { background: isMineToday ? 'rgba(37,99,235,0.18)' : 'transparent', border: `1px solid ${isMineToday ? BLUE : BORDER}`, color: isMineToday ? '#93C5FD' : TEXT } }}
              >
                My jobs · today
              </Button>
            )}
          </Group>
          <SegmentedControl
            size="xs"
            value={view}
            onChange={(v) => setView(v as ViewName)}
            data={VIEWS}
            styles={{ root: { background: CARD_BG, border: `1px solid ${BORDER}` }, label: { color: TEXT_MUTED }, indicator: { background: 'rgba(37,99,235,0.25)' } }}
          />
        </Group>
        <ConversationFilterBar value={filters} onChange={setFilters} />
      </Box>

      {/* Scrollable content (AppShell.Main is fixed-height + overflow:hidden) */}
      <Box style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '4px 20px 20px' }}>
        {isLoading ? (
          view === 'list' || view === 'stats' ? <Center py={48}><Loader size="sm" color="blue" /></Center> : <BoardSkeleton />
        ) : empty ? (
          <EmptyState filters={filters} />
        ) : view === 'list' ? (
          <ListView rows={rows} members={memberList} onOpen={open} onReassign={reassign} onBulkReassign={bulkReassign} onBulkArchive={bulkArchive} />
        ) : view === 'stats' ? (
          <>
            <TotalsStrip rows={rows} total={board?.total ?? rows.length} />
            <ListView rows={rows} members={memberList} onOpen={open} onReassign={reassign} onBulkReassign={bulkReassign} onBulkArchive={bulkArchive} />
          </>
        ) : view === 'per-stage' ? (
          <DndContext sensors={sensors} onDragEnd={(e) => onDragEnd(e, 'stage')}>
            <Box style={boardRowStyle}>
              {STAGES.map((s) => (
                <BoardColumn key={s.key} colId={`stage:${s.key}`} name={s.label} accent={s.color} rows={stageCols.get(s.key)!} members={memberList} onOpen={open} onReassign={reassign} draggable />
              ))}
            </Box>
          </DndContext>
        ) : view === 'per-charger' ? (
          <DndContext sensors={sensors} onDragEnd={(e) => onDragEnd(e, 'charger')}>
            <Box style={boardRowStyle}>
              {chargerCols.map((c) => (
                <BoardColumn key={c.name} colId={`tech:${c.name}`} name={c.name} rows={c.list} members={memberList} onOpen={open} onReassign={reassign} draggable />
              ))}
            </Box>
          </DndContext>
        ) : (
          <CalendarView rows={rows} dateBasis={filters.dateBasis} from={calFrom} to={calTo} onOpen={open} onPickDay={pickDay} />
        )}
      </Box>
    </Box>
  );
}
